/**
 * Справочник грузовой и коммерческой техники.
 *
 * Зачем список, а не свободный ввод: «ман», «MAN», «Ман TGX» и «MAN TGX 18.440»
 * в парке одной конторы — четыре разные машины, по которым нельзя ни отобрать,
 * ни отчитаться. Заказчику на выгрузке тоже нужна не строка из чужой головы,
 * а «белый MAN, номер такой-то».
 *
 * Что здесь есть и чего нет. Это рабочий перечень того, что реально возит
 * в России: европейская большая семёрка, отечественные и белорусские марки,
 * китайцы, заметно занявшие рынок после двадцать второго года, плюс лёгкая
 * коммерческая техника. Полного каталога всех модификаций тут нет и быть
 * не может — он живёт в платных отраслевых базах и устаревает быстрее, чем
 * его успевают переписать. Поэтому список моделей — семейства, а не
 * комплектации: «Actros», а не «Actros 1845 LS 4x2».
 *
 * `OTHER` в конце каждой ветки обязателен: техника, которой нет в списке,
 * существует, и заводить её должно быть можно без правки кода.
 */
export const OTHER_MODEL = 'Другая модель';
/**
 * Марки по группам. Порядок групп — по тому, как часто встречаются
 * в объявлениях: сначала то, что человек ищет глазами.
 */
export const TRUCK_BRANDS = [
    {
        group: 'Магистральные тягачи и грузовики',
        makes: [
            'Volvo',
            'Scania',
            'Mercedes-Benz',
            'MAN',
            'DAF',
            'Renault Trucks',
            'Iveco',
        ],
    },
    {
        group: 'Отечественные и СНГ',
        makes: ['КАМАЗ', 'МАЗ', 'ГАЗ', 'УРАЛ', 'ЗИЛ', 'КрАЗ', 'Соллерс'],
    },
    {
        group: 'Китайские',
        makes: [
            'Sitrak',
            'Shacman',
            'Howo',
            'FAW',
            'Dongfeng',
            'JAC',
            'Foton',
            'Sany',
            'Changan',
        ],
    },
    {
        group: 'Азиатские',
        makes: ['Isuzu', 'Hino', 'Hyundai', 'Mitsubishi Fuso', 'Kia'],
    },
    {
        group: 'Лёгкий коммерческий',
        makes: ['ГАЗель', 'Ford', 'Volkswagen', 'Peugeot', 'Citroen', 'Fiat', 'Lada'],
    },
];
/** Плоский список марок — для проверки и для выпадающего списка. */
export const TRUCK_MAKES = TRUCK_BRANDS.flatMap((g) => g.makes);
/**
 * Модели по маркам.
 *
 * Семейства, а не комплектации: диспетчеру нужно отличить тягач от самосвала,
 * а не 18.440 от 18.480. Год выпуска задаётся отдельным полем — держать
 * «Actros 2019» строкой значит потерять возможность отсортировать по возрасту.
 */
export const TRUCK_MODELS = {
    Volvo: ['FH', 'FH16', 'FM', 'FMX', 'FE', 'FL'],
    Scania: ['R-series', 'S-series', 'G-series', 'P-series', 'XT'],
    'Mercedes-Benz': ['Actros', 'Arocs', 'Atego', 'Axor', 'Antos', 'Sprinter'],
    MAN: ['TGX', 'TGS', 'TGM', 'TGL', 'TGE'],
    DAF: ['XF', 'XG', 'CF', 'LF'],
    'Renault Trucks': ['T', 'C', 'K', 'D', 'Master'],
    Iveco: ['S-Way', 'Stralis', 'Trakker', 'Eurocargo', 'Daily'],
    КАМАЗ: ['54901 (K5)', '5490 (NEO)', '65207', '65115', '65117', '43118', '4308'],
    МАЗ: ['5440', '6430', '6312', '5340', '4371 «Зубрёнок»'],
    ГАЗ: ['ГАЗон NEXT', 'Валдай NEXT', 'Садко NEXT', 'ГАЗель NEXT'],
    УРАЛ: ['4320', 'NEXT', '63704'],
    ЗИЛ: ['5301 «Бычок»', '4331'],
    КрАЗ: ['65055', '6510'],
    Соллерс: ['Atlant', 'Argo'],
    Sitrak: ['C7H', 'C9H', 'T7H'],
    Shacman: ['X3000', 'X5000', 'X6000', 'F3000'],
    Howo: ['T5G', 'T7H', 'A7', 'NX'],
    FAW: ['J6', 'J7', 'JH6', 'Tiger'],
    Dongfeng: ['GX', 'KX', 'DFH', 'Captain'],
    JAC: ['N75', 'N90', 'N120', 'K7'],
    Foton: ['Aumark', 'Auman', 'Ollin', 'Tunland'],
    Sany: ['Тягач', 'Самосвал'],
    Changan: ['Star Truck'],
    Isuzu: ['NQR', 'NPR', 'ELF', 'FORWARD', 'GIGA'],
    Hino: ['300', '500', '700'],
    Hyundai: ['HD35', 'HD65', 'HD78', 'HD120', 'Mighty', 'Porter'],
    'Mitsubishi Fuso': ['Canter', 'Fighter'],
    Kia: ['Bongo'],
    ГАЗель: ['Next', 'Business', 'City', 'Валдай'],
    Ford: ['Transit', 'Cargo'],
    Volkswagen: ['Crafter', 'Transporter'],
    Peugeot: ['Boxer', 'Partner'],
    Citroen: ['Jumper', 'Berlingo'],
    Fiat: ['Ducato', 'Doblo'],
    Lada: ['Largus'],
};
/**
 * Марки прицепной техники. Отдельно от тягачей: у прицепа своя индустрия,
 * и Schmitz с Volvo в одном списке сбивает с толку.
 */
export const TRAILER_MAKES = [
    'Schmitz Cargobull',
    'Krone',
    'Kögel',
    'Wielton',
    'Grunwald',
    'Тонар',
    'НефАЗ',
    'МАЗ',
    'СЗАП',
    'Bodex',
    'Kässbohrer',
    'Feldbinder',
    'Politrailer',
];
/** Цвет — то, по чему машину узнают на площадке раньше, чем читают номер. */
export const VEHICLE_COLORS = [
    'Белый',
    'Синий',
    'Красный',
    'Жёлтый',
    'Зелёный',
    'Серый',
    'Чёрный',
    'Оранжевый',
    'Серебристый',
    'Другой',
];
/**
 * Годы выпуска для выпадающего списка.
 *
 * От текущего года вниз до 1990: техника старше тридцати лет на межгороде
 * почти не встречается, а список из сотни позиций листать невозможно.
 */
export function vehicleYears(now = new Date().getFullYear()) {
    const out = [];
    // На год вперёд: машины следующего модельного года продаются с осени.
    for (let y = now + 1; y >= 1990; y -= 1)
        out.push(y);
    return out;
}
/** Модели марки. Для марки не из справочника — пустой список и ручной ввод. */
export function modelsOf(make) {
    return TRUCK_MODELS[make] ?? [];
}
/** Как машина называется в одну строку: «белый MAN TGX · А123ВС777». */
export function vehicleTitle(v) {
    const name = [v.make, v.model].filter(Boolean).join(' ');
    const head = [v.color, name].filter(Boolean).join(' ');
    return head ? `${head} · ${v.plate}` : v.plate;
}
